import { useEffect, useState } from "react";
import { portfolio } from "../data/portfolio";
import { SectionHead } from "../components/SectionHead";

function fmtBytes(b: number | null) {
  if (b == null) return null;
  if (b < 1024) return b + " B";
  if (b < 1024 * 1024) return (b / 1024).toFixed(1) + " KB";
  return (b / 1024 / 1024).toFixed(2) + " MB";
}

function fmtDate(d: Date | null) {
  if (!d) return null;
  return d.toLocaleString("en-US", { month: "long", year: "numeric" });
}

export function Resume() {
  const { identity } = portfolio;
  const cv = identity.links.cv;
  const fileName = cv.split("/").pop() ?? "Resume.pdf";

  const [bytes, setBytes] = useState<number | null>(null);
  const [updated, setUpdated] = useState<Date | null>(null);
  const [supported, setSupported] = useState(true);

  useEffect(() => {
    if (/iPad|iPhone|iPod/.test(navigator.userAgent)) setSupported(false);
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(cv, { method: "HEAD" });
        if (cancelled) return;
        const len = res.headers.get("content-length");
        const mod = res.headers.get("last-modified");
        if (len) setBytes(parseInt(len, 10));
        if (mod) setUpdated(new Date(mod));
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cv]);

  return (
    <section id="resume" className="md-section">
      <div className="md-container">
        <SectionHead
          num="05"
          eyebrow="Résumé"
          title="Page, on file."
          dek="The whole portfolio compressed into the format hiring managers actually read."
        />

        <div className="m3-resume__grid">
          <div className="md-card md-card--outlined m3-resume__preview reveal">
            <div className="m3-resume__chrome">
              <div className="md-label-m">{fileName}</div>
              <a
                className="md-btn md-btn--icon"
                href={cv}
                target="_blank"
                rel="noreferrer"
                aria-label="Open in new tab"
              >
                <span className="material-symbols-rounded icon-20">open_in_new</span>
              </a>
            </div>
            <div className="m3-resume__viewer">
              {supported ? (
                <iframe
                  className="m3-resume__iframe"
                  src={cv + "#view=FitH&toolbar=0&navpanes=0"}
                  title="Résumé preview"
                />
              ) : (
                <div className="m3-resume__fallback">
                  <span
                    className="material-symbols-rounded icon-40"
                    style={{ color: "var(--md-sys-color-primary)" }}
                  >
                    picture_as_pdf
                  </span>
                  <div className="md-body-l" style={{ textAlign: "center" }}>
                    Inline preview isn&rsquo;t available on this browser.
                  </div>
                  <a className="md-btn md-btn--filled" href={cv} target="_blank" rel="noreferrer">
                    Open résumé
                    <span className="material-symbols-rounded">arrow_outward</span>
                  </a>
                </div>
              )}
            </div>
          </div>

          <aside className="m3-resume__sticky reveal">
            <div className="md-card md-card--filled md-card__pad">
              <div className="md-label-m dim">File</div>
              <div className="md-title-l" style={{ marginTop: 4 }}>
                {identity.name}
              </div>
              <div className="md-body-s dim">Résumé · v2026.05</div>

              <dl className="m3-resume__facts md-body-m">
                <div className="m3-resume__fact">
                  <dt className="md-label-m">Format</dt>
                  <dd>PDF · A4</dd>
                </div>
                <div className="m3-resume__fact">
                  <dt className="md-label-m">Size</dt>
                  <dd>{fmtBytes(bytes) || "—"}</dd>
                </div>
                <div className="m3-resume__fact">
                  <dt className="md-label-m">Updated</dt>
                  <dd>{fmtDate(updated) || "May 2026"}</dd>
                </div>
                <div className="m3-resume__fact">
                  <dt className="md-label-m">Pages</dt>
                  <dd>1–2</dd>
                </div>
              </dl>

              <div className="m3-resume__actions">
                <a className="md-btn md-btn--filled" href={cv} download>
                  <span className="material-symbols-rounded">download</span>
                  Download résumé
                </a>
                <a className="md-btn md-btn--tonal" href={cv} target="_blank" rel="noreferrer">
                  <span className="material-symbols-rounded">open_in_new</span>
                  Open in new tab
                </a>
              </div>
            </div>

            <div className="md-card md-card--outlined md-card__pad">
              <div className="md-label-m dim">At a glance</div>
              <ul
                className="md-list"
                style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}
              >
                {[
                  ["work", "AI/ML Research Engineer · Coimbatore, India"],
                  ["forum", "Production multi-agent LLM systems · RAG · retrieval"],
                  ["bolt", "Edge inference · CUDA · TensorRT · llama.cpp"],
                  ["star", "Published at IEEE · spoken at Airbus Global AI Week"],
                ].map(([icon, text], i) => (
                  <li
                    key={i}
                    style={{ display: "grid", gridTemplateColumns: "20px 1fr", gap: 12, alignItems: "start" }}
                  >
                    <span
                      className="material-symbols-rounded icon-20"
                      style={{ color: "var(--md-sys-color-primary)" }}
                    >
                      {icon}
                    </span>
                    <span className="md-body-m">{text}</span>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}
